import type { StateCreator } from "zustand";

import {
  addOrUpdateState,
  getOrderCodeState,
} from "components/_ProductConfigurator/_helpers";
import { createProductMutStr } from "api/mutations";
import { NodeKinds } from "interfaces/enums";
import { getAccessoriesData } from "utils";
import { fetchData } from "hooks";
import type { productConfiguratorTabsEnum, Unit } from "variables/enums";
import type {
  ChangeProductPayload,
  ProductConfiguratorStore,
  ProductConfiguratorStoreActions,
  ProductRequestData,
} from "components/_ProductConfigurator/store/ProductConfiguration.types";
import { toast } from "react-toastify";
import { GetProductStateStr } from "api/queries";
import { handleLoadProductState } from "components/_ProductConfigurator/store/ProductStoreHelpers";
import { initialState } from "./ProductConfiguratorStore";
import * as Sentry from "@sentry/react";
import isEqual from "lodash-es/isEqual";

export const productConfiguratorStoreActions: StateCreator<
  ProductConfiguratorStore,
  [],
  [],
  ProductConfiguratorStoreActions
> = (set, get) => ({
  loadDefaultState: ({ seriesId, language, units, country, topologyData }) => {
    fetchData({
      query: createProductMutStr,
      variables: {
        input: {
          seriesId,
          languageCode: language,
          resultUnitsSystem: units,
          requestUnitsSystem: units,
          countryCode: country,
          ...(topologyData
            ? {
                wizardType: topologyData?.wizardType,
                dummyType: topologyData?.dummyType,
                groupPosition: topologyData?.groupPosition,
              }
            : {}),
        },
      },
      onSuccess: ({ data }) => {
        const { result } = data.createProduct;
        const state = handleLoadProductState({
          result,
          seriesId,
          language,
          country,
          units,
          topologyData,
        });

        set(state);
      },
      onError: (err) => console.error("Error loading default state", err),
    });
  },
  setActivePartModal: (part: string) => set({ activePartModal: part }),
  setActivePart: (part: string) => set({ activePart: part }),
  setIsConfigurationModalOpened: (val: boolean) =>
    set({ isConfigurationModalOpened: val }),
  setIsProductChanged: (val: boolean) => set({ isProductChanged: val }),

  loadPersistedState: ({
    nodeId,
    language,
    seriesId,
    units,
    country,
    topologyData,
    productName,
  }) => {
    fetchData({
      query: GetProductStateStr,
      variables: {
        input: {
          nodeId,
          languageCode: language,
          countryCode: country,
          resultUnitsSystem: units,
          requestUnitsSystem: units,
          ...(topologyData
            ? {
                wizardType: topologyData?.wizardType,
                dummyType: topologyData?.dummyType,
                groupPosition: topologyData?.groupPosition,
              }
            : {}),
        },
      },
      onSuccess: ({ data }) => {
        const { result } = data.getProductState;
        const state = handleLoadProductState({
          productId: nodeId,
          result,
          seriesId,
          language,
          country,
          units,
          topologyData,
          productName,
        });

        set(state);
      },
      onError: (err) => toast.error(err.message),
    });
  },

  clear: () => {
    set(initialState);
  },

  getProductData: async (isProductChanged?: boolean) => {
    const { requestParams, productName, productData } = get();

    if (!requestParams) {
      return;
    }
    set({ isLoading: true });

    fetchData({
      query: createProductMutStr,
      variables: {
        input: { ...requestParams },
      },
      onSuccess: ({ data }) => {
        if (!data?.createProduct.result?.product) {
          const errorMessage =
            "The selected configuration is not available. Please change your selection.";
          toast.error(errorMessage);
          Sentry.captureMessage(errorMessage, {
            level: "error",
            extra: {
              requestParams,
            },
          });
          set({ isLoading: false });
          return;
        }
        if (data?.createProduct) {
          const prevListViewConfiguration =
            productData?.product?.listViewConfiguration;
          const nextListViewConfiguration =
            data.createProduct.result.product.listViewConfiguration;

          set({
            productData: {
              ...data.createProduct.result,
              product: {
                ...data.createProduct.result?.product,
                listViewConfiguration: isEqual(
                  prevListViewConfiguration,
                  nextListViewConfiguration,
                )
                  ? prevListViewConfiguration
                  : nextListViewConfiguration,
              },
            },
            productName:
              productName ?? data.createProduct.result.product.orderCode.key,
            isLoading: false,
          });
          if (typeof isProductChanged !== "undefined") {
            set({ isProductChanged: isProductChanged ? true : false });
          }
        }
      },
      onError: (err) => {
        if (err?.message) {
          toast.error(err.originalError.message, {
            type: "error",
            theme: "colored",
          });
          Sentry.captureMessage(err.originalError.message, {
            level: "error",
            extra: {
              err,
            },
          });
        }
        set({ isLoading: false });
      },
    });
  },

  changeProduct: ({
    newStrategyId = null,
    changeData = {},
    changeInput = {},
    newSelectedAccessories,
    isAccessoryAffected = false,
  }: ChangeProductPayload) => {
    const { data, partKey, optionKey } = changeData;
    const { inputValue, inputId } = changeInput;

    set((state) => {
      if (!state.requestParams || !state.productData) {
        return {};
      }

      const lastChange =
        partKey && optionKey
          ? {
              partKey,
              optionKey,
              signatureKey: data || "",
            }
          : null;

      const previousState = state.requestParams.state || {};

      const newPropertyStates = inputId
        ? addOrUpdateState({
            array: previousState.propertyStates || [],
            value: { id: inputId, value: inputValue ?? "" },
          })
        : previousState.propertyStates || [];

      return {
        requestParams: {
          ...state.requestParams,
          state: {
            ...previousState,
            orderCodeState: {
              lastChange,
              optionStates: getOrderCodeState(
                state.productData.product.orderCode.parts,
              ),
            },
            propertyStates: newPropertyStates,
            currentStrategyId: newStrategyId || previousState.currentStrategyId,
            selectedAccessories:
              isAccessoryAffected || newSelectedAccessories
                ? newSelectedAccessories
                : previousState.selectedAccessories,
          },
        },
      };
    });

    get().actions.getProductData(true);
  },

  onSaveConfiguration: () => {
    const { requestParams } = get();
    set({ initialRequestParams: structuredClone(requestParams) });
  },

  resetData: () => {
    const { initialRequestParams } = get();
    const firstElement = document.getElementById("part-id-01");
    if (firstElement) {
      firstElement.scrollIntoView({
        behavior: "smooth",
      });
    }
    set({ requestParams: initialRequestParams });
    get().actions.getProductData(false);
  },

  changeUnits: (_newUnits: Unit) => {
    const { requestParams } = get();
    if (!requestParams) {
      return;
    }

    get().actions.getProductData();
  },

  getProductRequestData: () => {
    const {
      requestParams,
      productId,
      initialRequestParams,
      productData,
      lang,
      productName,
    } = get();

    if (!productId || !productData || !requestParams) {
      return null;
    }

    const optionStates = getOrderCodeState(productData.product.orderCode.parts);
    const seriesId = initialRequestParams?.seriesId;

    const productImagePimId =
      productData.productImageList?.productImages.filter(
        (image: {
          isReportImage: boolean;
          pimId: string;
          text: string;
          typ: "ProductPhoto" | string;
        }) => image.typ === "ProductPhoto",
      )[0]?.pimId;

    const accessoriesData = getAccessoriesData(
      productData.product.selectedAccessories,
      productData.product.accessoryGroups,
    );

    const req: ProductRequestData = {
      id: productId,
      kind: NodeKinds.PRODUCT,
      orderCode: {
        seriesId: Number(seriesId),
        state: {
          optionStates,
        },
        currentStrategyId: requestParams.state?.currentStrategyId,
        propertyStates: requestParams.state?.propertyStates?.map((ps) => ({
          ...ps,
          value: String(ps.value),
        })),
      },
      productName: productName || productData.product.orderCode.key,
      presentation: productData.product.orderCode.presentation,
      pimId: productImagePimId,
      languageCode: lang,
      selectedAccessories: accessoriesData,
    };

    return req;
  },
  changeActiveTab: (newTab: productConfiguratorTabsEnum) => {
    set({ activeTab: newTab });
  },
  changeProductToSuggested: ({
    suggestedProductOrderCode,
    selectedAccessories,
  }) => {
    set((state) => {
      if (!state.requestParams) {
        console.warn("Something goes wrong `changeProductToSuggested`");
        return {};
      }

      const previousState = state.requestParams.state || {};

      return {
        requestParams: {
          ...state.requestParams,
          state: {
            ...previousState,
            orderCodeState: suggestedProductOrderCode,
            selectedAccessories,
          },
        },
      };
    });

    get().actions.getProductData();
  },
});
